import type { BlockNode } from '@personalizehq/schemas/ir';
import { newId } from './doc.js';

// Tree ops over the block tree. Every one of these is written to run INSIDE an immer draft
// (see session.ts `applyChange`) — they mutate, and the store turns that into a patch pair.
//
// `hit` is the only walker. Everything else is expressed in terms of it, so there is exactly one
// place that knows how a parent/child relationship is discovered.

/** The four containers, i.e. the blocks with a `children[]` the author can insert into.
 *  `repeat` is NOT one: its `template` is a single slot, and authoring inside it is a later slice
 *  (p3-authoring-dashboard.md §3). It still gets its ids rewritten by `duplicateNode`. */
export type ContainerNode = Extract<BlockNode, { children: BlockNode[] }>;

export function isContainer(node: BlockNode): node is ContainerNode {
  return (
    node.block === 'stack' ||
    node.block === 'box' ||
    node.block === 'scroll' ||
    node.block === 'grid'
  );
}

export interface Hit {
  node: BlockNode;
  /** null for the root. */
  parent: ContainerNode | null;
  /** -1 for the root. */
  index: number;
}

/** Depth-first search of the EDITABLE tree — children only, never into a `fallback` or a
 *  `repeat.template`. Those are separate authoring surfaces (E5 / later slice), and letting a
 *  selection or a move land inside one from the layers tree would be a silent structural bug. */
export function findNode(root: BlockNode, id: string): Hit | undefined {
  if (root.id === id) return { node: root, parent: null, index: -1 };
  if (!isContainer(root)) return undefined;

  for (let i = 0; i < root.children.length; i++) {
    const child = root.children[i]!;
    if (child.id === id) return { node: child, parent: root, index: i };
    const deeper = findNode(child, id);
    if (deeper) return deeper;
  }
  return undefined;
}

/** Every id in the subtree, INCLUDING the ones inside `fallback` and `repeat.template` — those
 *  are real nodes on the wire, so a duplicate must not clone their ids. */
export function collectIds(node: BlockNode, into: Set<string> = new Set()): Set<string> {
  into.add(node.id);
  if (isContainer(node)) for (const c of node.children) collectIds(c, into);
  if (node.block === 'repeat') collectIds(node.template, into);
  if (node.fallback) collectIds(node.fallback, into);
  return into;
}

export function isDescendant(root: BlockNode, ancestorId: string, maybeDescendantId: string): boolean {
  const hit = findNode(root, ancestorId);
  if (!hit) return false;
  if (ancestorId === maybeDescendantId) return true;
  return collectIds(hit.node).has(maybeDescendantId);
}

export function insertNode(root: BlockNode, parentId: string, index: number, node: BlockNode): void {
  const hit = findNode(root, parentId);
  if (!hit) throw new Error(`insert: unknown parent ${parentId}`);
  if (!isContainer(hit.node)) throw new Error(`insert: ${hit.node.block} is not a container`);
  const at = clampIndex(index, hit.node.children.length);
  hit.node.children.splice(at, 0, node);
}

export function removeNode(root: BlockNode, id: string): void {
  const hit = findNode(root, id);
  if (!hit) throw new Error(`remove: unknown node ${id}`);
  if (!hit.parent) throw new Error('remove: the root cannot be removed');
  hit.parent.children.splice(hit.index, 1);
}

/** Move within the tree. The two guards are the whole reason this is not two lines of splice:
 *  a node cannot move into its own subtree (it would vanish from the doc), and removing it from
 *  its old slot SHIFTS the target index when both are in the same parent. */
export function moveNode(root: BlockNode, id: string, newParentId: string, index: number): void {
  const hit = findNode(root, id);
  if (!hit) throw new Error(`move: unknown node ${id}`);
  if (!hit.parent) throw new Error('move: the root cannot be moved');
  if (isDescendant(root, id, newParentId)) throw new Error('move: cannot move into own subtree');

  const target = findNode(root, newParentId);
  if (!target) throw new Error(`move: unknown parent ${newParentId}`);
  if (!isContainer(target.node)) throw new Error(`move: ${target.node.block} is not a container`);

  const sameParent = target.node === hit.parent;
  let at = clampIndex(index, target.node.children.length);
  if (sameParent && hit.index < at) at -= 1;

  hit.parent.children.splice(hit.index, 1);
  target.node.children.splice(at, 0, hit.node);
}

/** Insert a fresh-id deep clone directly after the original. Ids are regenerated across the WHOLE
 *  subtree — children, `fallback`s and `repeat.template`s alike — because a duplicated id is a
 *  duplicated `data-phq-id`, and the renderer's hit-testing and the conformance snapshot both key
 *  on it. */
export function duplicateNode(root: BlockNode, id: string): BlockNode {
  const hit = findNode(root, id);
  if (!hit) throw new Error(`duplicate: unknown node ${id}`);
  if (!hit.parent) throw new Error('duplicate: the root cannot be duplicated');

  const clone = cloneWithFreshIds(hit.node);
  hit.parent.children.splice(hit.index + 1, 0, clone);
  return clone;
}

export function cloneWithFreshIds(node: BlockNode): BlockNode {
  // ponytail: JSON round-trip, not structuredClone — the IR is JSON by definition (it is what goes
  // on the wire), and this reads cleanly through an immer draft's proxy where structuredClone's
  // internal-slot cloning is not guaranteed to.
  const clone = JSON.parse(JSON.stringify(node)) as BlockNode;
  reid(clone);
  return clone;
}

function reid(node: BlockNode): void {
  node.id = newId();
  if (isContainer(node)) for (const c of node.children) reid(c);
  if (node.block === 'repeat') reid(node.template);
  if (node.fallback) reid(node.fallback);
}

function clampIndex(index: number, length: number): number {
  if (!Number.isFinite(index)) return length;
  return Math.max(0, Math.min(Math.trunc(index), length));
}
